import type { CSSProperties } from 'react'
import type { MemoryClip } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

import {
    Pause,
    Play,
    RotateCcw,
    Volume2,
    VolumeX
} from 'lucide-react'

type ClipState = 'idle' | 'playing' | 'paused' | 'ended'

const quarterOf = (rotation: number): number => (((Math.round(rotation / 90)) % 4) + 4) % 4

interface ClipsPlayerProps {
    clips: Array<MemoryClip>
}

/**
 * Plays the uploaded video clips one after another, story style. The frame
 * takes the shape of each clip, and sideways clips are turned upright.
 */
export default function ClipsPlayer({ clips }: ClipsPlayerProps) {
    const [state, setState] = useState<ClipState>('idle')
    const [index, setIndex] = useState(0)
    const [muted, setMuted] = useState(false)
    const [progress, setProgress] = useState(0)
    const [ratios, setRatios] = useState<Record<string, number>>({})
    const videoRef = useRef<HTMLVideoElement>(null)
    const stageRef = useRef<HTMLDivElement>(null)

    const total = clips.length
    const clip = clips[Math.min(index, total - 1)]
    const running = state === 'playing' || state === 'paused'

    useEffect(() => {
        const video = videoRef.current

        if (!video) return

        if (state !== 'playing') {
            video.pause()

            return
        }

        video.play().catch(() => {
            video.muted = true
            setMuted(true)
            video.play().catch(() => setState('paused'))
        })
    }, [state, index])

    useEffect(() => {
        if (videoRef.current) videoRef.current.muted = muted
    }, [muted])

    useEffect(() => {
        const node = stageRef.current

        if (!node || state !== 'playing' || typeof IntersectionObserver === 'undefined') return

        const observer = new IntersectionObserver(entries => {
            if (entries.some(entry => !entry.isIntersecting)) setState('paused')
        }, { threshold: 0.3 })

        observer.observe(node)

        return () => observer.disconnect()
    }, [state])

    if (!clip?.video) return null

    const quarter = quarterOf(clip.video_rotation)
    const sideways = quarter === 1 || quarter === 3
    const ratio = ratios[clip.id] ?? (sideways ? 16 / 9 : 9 / 16)

    const start = () => {
        setIndex(0)
        setProgress(0)
        setState('playing')
    }

    const next = () => {
        if (index >= total - 1) {
            setState('ended')

            return
        }

        setProgress(0)
        setIndex(index + 1)
    }

    const previous = () => {
        if (index === 0) {
            if (videoRef.current) videoRef.current.currentTime = 0

            return
        }

        setProgress(0)
        setIndex(index - 1)
    }

    const toggle = () => {
        setState(value => value === 'playing' ? 'paused' : 'playing')
    }

    const stageStyle: CSSProperties = {
        aspectRatio: ratio,
        width: `min(100%, calc(72vh * ${ratio.toFixed(4)}))`
    }

    const videoStyle: CSSProperties = {
        width: sideways ? '100cqh' : '100%',
        height: sideways ? '100cqw' : '100%',
        transform: `translate(-50%, -50%) rotate(${quarter * 90}deg)`
    }

    return (
        <div
            ref={stageRef}
            className='relative mx-auto overflow-hidden rounded-[2rem] bg-[radial-gradient(circle_at_top,#4a1336_0%,#1a0916_75%)] shadow-[0_40px_90px_-40px_rgba(232,84,124,0.55)] ring-1 ring-white/10 transition-[width] duration-500'
            style={stageStyle}
        >
            <div className='absolute inset-0' style={{ containerType: 'size' }}>
                <video
                    ref={videoRef}
                    src={`${clip.video}#t=0.1`}
                    playsInline
                    preload='metadata'
                    muted={muted}
                    onTimeUpdate={event => {
                        const { currentTime, duration } = event.currentTarget

                        if (duration > 0) setProgress(Math.min(1, currentTime / duration))
                    }}
                    onLoadedMetadata={event => {
                        const { videoWidth, videoHeight } = event.currentTarget

                        if (!videoWidth || !videoHeight) return

                        const natural = sideways ? videoHeight / videoWidth : videoWidth / videoHeight

                        setRatios(current => current[clip.id] === natural ? current : { ...current, [clip.id]: natural })
                    }}
                    onEnded={next}
                    onError={() => {
                        if (state === 'playing') next()
                    }}
                    className='absolute top-1/2 left-1/2 max-w-none object-cover'
                    style={videoStyle}
                />
            </div>

            {state !== 'idle' && (
                <div aria-hidden className='absolute inset-x-4 top-4 z-20 flex gap-1.5'>
                    {clips.map((item, position) => {
                        const fill = position < index || state === 'ended' ? 1 : position === index ? progress : 0

                        return (
                            <span key={item.id} className='h-1 flex-1 overflow-hidden rounded-full bg-white/25'>
                                <span className='block h-full rounded-full bg-white' style={{ width: `${fill * 100}%` }} />
                            </span>
                        )
                    })}
                </div>
            )}

            {running && (
                <div className='absolute inset-x-0 top-0 bottom-28 z-10 flex'>
                    <button
                        type='button'
                        onClick={previous}
                        aria-label='Pichli clip'
                        className='h-full w-1/3 cursor-w-resize outline-none focus-visible:bg-white/5'
                    />

                    <button
                        type='button'
                        onClick={next}
                        aria-label='Agli clip'
                        className='h-full w-2/3 cursor-e-resize outline-none focus-visible:bg-white/5'
                    />
                </div>
            )}

            {running && (
                <div className='absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-night/90 via-night/40 to-transparent px-5 pt-16 pb-5 pr-28 sm:px-7 sm:pr-32 sm:pb-6'>
                    {clip.caption && (
                        <p
                            key={clip.id}
                            data-mdlfy-cms={`memory_clips:${clip.id}:caption`}
                            className='animate-in font-hand text-2xl leading-tight text-cream duration-700 fade-in slide-in-from-bottom-2'
                        >
                            {clip.caption}
                        </p>
                    )}

                    <p className='mt-1 text-xs font-semibold tracking-[0.25em] text-cream/60'>
                        {index + 1} / {total}
                    </p>
                </div>
            )}

            {running && (
                <div className='absolute right-4 bottom-4 z-20 flex gap-2 sm:right-5 sm:bottom-5'>
                    <button
                        type='button'
                        onClick={() => setMuted(value => !value)}
                        aria-label={muted ? 'Sound on karo' : 'Sound band karo'}
                        aria-pressed={!muted}
                        className='grid size-11 place-items-center rounded-full bg-white/15 text-cream ring-1 ring-white/25 backdrop-blur-md transition outline-none hover:bg-white/25 focus-visible:ring-4 focus-visible:ring-[#ffd1dc]/50'
                    >
                        {muted ? <VolumeX aria-hidden className='size-5' /> : <Volume2 aria-hidden className='size-5' />}
                    </button>

                    <button
                        type='button'
                        onClick={toggle}
                        aria-label={state === 'playing' ? 'Pause karo' : 'Play karo'}
                        className='grid size-11 place-items-center rounded-full bg-white/15 text-cream ring-1 ring-white/25 backdrop-blur-md transition outline-none hover:bg-white/25 focus-visible:ring-4 focus-visible:ring-[#ffd1dc]/50'
                    >
                        {state === 'playing'
                            ? <Pause aria-hidden className='size-5 fill-current' />
                            : <Play aria-hidden className='size-5 translate-x-px fill-current' />}
                    </button>
                </div>
            )}

            {state === 'idle' && (
                <div className='absolute inset-0 z-20 grid place-items-center bg-night/50 px-4'>
                    <button
                        type='button'
                        onClick={start}
                        className='group flex flex-col items-center gap-4 rounded-3xl p-4 text-center text-cream outline-none focus-visible:ring-4 focus-visible:ring-[#ffd1dc]/50'
                    >
                        <span className='relative grid size-20 place-items-center rounded-full bg-gradient-to-br from-petal to-petal-deep shadow-[0_18px_50px_-12px_rgba(232,84,124,0.9)] transition-transform group-hover:scale-105'>
                            <span aria-hidden className='absolute inset-0 animate-pulse-ring rounded-full bg-petal/60 motion-reduce:hidden' />

                            <Play aria-hidden className='relative size-8 translate-x-0.5 fill-current' />
                        </span>

                        <span className='text-lg font-bold tracking-wide'>
                            Clips chalao
                        </span>

                        <span className='-mt-3 text-sm text-cream/70'>
                            {total} chhoti clips
                        </span>
                    </button>
                </div>
            )}

            {state === 'ended' && (
                <div className='absolute inset-0 z-20 grid animate-in place-items-center bg-night/75 px-6 text-center duration-700 fade-in'>
                    <div>
                        <p className='font-heading text-xl text-balance text-cream/90 italic'>
                            Aur clips? Wo hum saath milke banayenge.
                        </p>

                        <button
                            type='button'
                            onClick={start}
                            className='mt-6 inline-flex items-center gap-2 rounded-full bg-cream px-6 py-3 font-bold text-wine shadow-lg transition outline-none hover:bg-white focus-visible:ring-4 focus-visible:ring-[#ffd1dc]/60'
                        >
                            <RotateCcw aria-hidden className='size-4' />
                            Phir se dekho
                        </button>
                    </div>
                </div>
            )}

            <span className={cn('sr-only', state === 'idle' && 'hidden')} aria-live='polite'>
                {state === 'ended' ? 'Saari clips khatam' : `Clip ${index + 1} / ${total}`}
            </span>
        </div>
    )
}
